import React, { useCallback, useState, useEffect, useRef } from "react";
import { useGetOrdersMutation } from "../api/api";
import {
  useDecodedTokenResult,
  formatDate,
  formatDateTime,
} from "../lib/utils";
import { pdf } from "@react-pdf/renderer";
import { PiExportBold } from "react-icons/pi";
import { PiFilePdfDuotone } from "react-icons/pi";
import { PiFileCsvDuotone } from "react-icons/pi";
import OrderPDFDocument from "./OrderPDFDocument";

interface Data {
  id: number;
  vendorId: number;
  vendorName: String;
  customerId: number;
  statusId: number;
  createdAt: Date;
  totalAmount: number;
  customerName: string;
  statusName: string;
  serviceFee: number;
  deliveryCharges: number;
}

interface ExportOrdersProps {
  selectedStatus: {
    name: string;
    id: number | undefined;
  };
  datefilter: {
    startDate?: Date;
    endDate?: Date;
  };
  vendorIdbyProp?: number;
  totalCount: number;
  sortingOrder?: "asc" | "desc";
  sortingColumn?: string;
}

const ExportOrders: React.FC<ExportOrdersProps> = ({
  selectedStatus,
  datefilter,
  vendorIdbyProp,
  totalCount,
  sortingOrder = "desc",
  sortingColumn = "createdAt",
}) => {
  const [getOrders] = useGetOrdersMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { vendorId, role } = useDecodedTokenResult();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchOrders = useCallback(
    async (
      statusId: number | undefined,
      datefilter: { startDate?: Date; endDate?: Date }
    ) => {
      try {
        const allOrders: Data[] = [];
        const response: any = await getOrders({
          getOrderRequestDto: {
            vendorId:
              role === "ADMIN"
                ? vendorIdbyProp
                  ? vendorIdbyProp
                  : undefined
                : vendorId,
            pageNumber: 1,
            pageSize: totalCount,
            statusId: statusId,
            sortingOrder: sortingOrder,
            sortingColumn: sortingColumn,
            startDate: datefilter.startDate
              ? formatDateTime(datefilter.startDate, "YYYY-MM-DDTHH:mm:ssZ")
              : undefined,
            endDate: datefilter.endDate
              ? formatDateTime(datefilter.endDate, "YYYY-MM-DDTHH:mm:ssZ")
              : undefined,
          },
        }).unwrap();

        allOrders.push(...(response.data.orders || []));

        return allOrders;
      } catch (err) {
        console.error("Failed to get orders: ", err);
        return [];
      }
    },
    [getOrders, vendorId, role, sortingOrder, sortingColumn]
  );

  const handleExportOrders = async (
    type: "csv" | "pdf",
    statusId: number | undefined,
    datefilter: { startDate?: Date; endDate?: Date }
  ) => {
    setIsLoading(true);
    const allOrders = await fetchOrders(statusId, datefilter);

    // Update headers based on role
    const headers = [
      "Order Number",
      "Date",
      "Customer Name",
      ...(role === "ADMIN" ? ["Vendor Name"] : []),
      "Service Fee",
      "Total Amount",
      ...(role === "ADMIN" ? ["Delivery Fee"] : []), // Only include Delivery Fee for admin
      "Status Name",
    ];

    const rows = allOrders.map((item) => {
      return [
        item.id,
        formatDate(new Date(item.createdAt), "dd MMM yyyy"),
        item.customerName,
        ...(role === "ADMIN"
          ? [item.vendorName ? item.vendorName : "---"]
          : []),
        item.serviceFee.toFixed(2),
        item.totalAmount.toFixed(2),
        ...(role === "ADMIN"
          ? [item.deliveryCharges ? item.deliveryCharges.toFixed(2) : "---"]
          : []),
        item.statusName,
      ];
    });

    if (type === "csv") {
      // Ensure Delivery Fee column is included in CSV
      const csvContent = [headers, ...rows]
        .map((row) => row.join(","))
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `orders_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === "pdf") {
      // Calculate totals from allOrders
      const totals = allOrders.reduce(
        (acc, order) => ({
          totalAmount: acc.totalAmount + order.totalAmount,
          serviceFeeTotal: acc.serviceFeeTotal + order.serviceFee,
          deliveryFeeTotal: acc.deliveryFeeTotal + (order.deliveryCharges || 0),
          netTotal:
            role === "ADMIN"
              ? acc.netTotal + order.totalAmount
              : acc.netTotal + order.totalAmount,
        }),
        {
          totalAmount: 0,
          serviceFeeTotal: 0,
          deliveryFeeTotal: 0,
          netTotal: 0,
        }
      );

      const exportInfo = {
        totalOrders: allOrders.length,
        dateRange: `${formatDate(new Date(), "dd MMM yyyy")}`,
        statusName: selectedStatus.name,
        totals,
        userRole: role,
      };

      const blob = await pdf(
        <OrderPDFDocument
          headers={headers}
          rows={rows}
          exportInfo={exportInfo}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `orders_${new Date().toISOString()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    setIsLoading(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 gap-1 rounded-lg w-full sm:w-auto"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <PiExportBold className="w-4 h-4" />
        <div>{isLoading ? "Downloading..." : "Export"}</div>
      </button>

      {dropdownOpen && (
        <div
          className="sm:right-1/2 sm:translate-x-1/2"
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            marginTop: "0.5rem",
            width: "max-content",
            backgroundColor: "white",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "0.375rem",
            zIndex: 10,
            whiteSpace: "nowrap",
          }}
        >
          <div className="py-1">
            <button
              className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              onClick={() => {
                handleExportOrders("pdf", selectedStatus.id, datefilter);
                setDropdownOpen(false);
              }}
            >
              <PiFilePdfDuotone className="w-5 h-5" />
              Export as PDF
            </button>
            <button
              className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              onClick={() => {
                handleExportOrders("csv", selectedStatus.id, datefilter);
                setDropdownOpen(false);
              }}
            >
              <PiFileCsvDuotone className="w-5 h-5" />
              Export as CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportOrders;
